import { Grid, Paper, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { OrderedQuestion } from '../../../pages/motive-survey';
import Question from './question';
import { AnswerType } from '../survey.types';

interface Props {
  title?: string;
  questions: OrderedQuestion[];
  selectedValue: number[];
  missingQuestionIndexes: number[];
  answerType: string;
  selectionChangeHandlerGenerator(shuffledIdx: number, realIdx: number, realType: string): (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const APPLIES_LABELS = ['notApplyAtAll', 'notApply', 'ratherNotApply', 'ratherApply', 'quiteApply', 'appliesPerfectly'];
const IMPORTANCE_LABELS = ['notImportant', 'littleImportant', 'somethingImportant', 'important', 'veryImportant', 'extremelyImportant'];

const QuestionContainer: React.FC<Props> = ({
  title,
  selectedValue,
  selectionChangeHandlerGenerator,
  questions,
  answerType,
  missingQuestionIndexes,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const labels = answerType === AnswerType.APPLIES ? APPLIES_LABELS : IMPORTANCE_LABELS;

  return (
    <Paper className={classes.paper} elevation={0}>
      <Grid container spacing={2} direction="column">
        {title && (
          <Grid>
            <Typography variant="h4" noWrap>{title}</Typography>
          </Grid>
        )}
        <Grid>
          <Grid container direction="row" spacing={2} justifyContent="space-between" alignItems="flex-end">
            <Grid size={6} />
            {labels.map((labelKey) => (
              <Grid size={1} key={labelKey} className={classes.headerLabel}>
                <Typography variant="body2">{t(labelKey)}</Typography>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid size={12}>
          <Grid container spacing={2} justifyContent="space-between">
            {questions.map((question) => (
              <Question
                key={question.order.orderedIndex}
                index={question.order.orderedIndex}
                question={question}
                selectionChangeHandlerGenerator={selectionChangeHandlerGenerator}
                selectedValue={selectedValue[question.order.orderedIndex]}
                hasError={missingQuestionIndexes.includes(question.order.orderedIndex)}
              />
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(3),
    border: `2px solid ${theme.palette.secondary.main}`,
    borderRadius: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  headerLabel: {
    textAlign: 'center',
  },
}));

export default React.memo(QuestionContainer);
